import { AlertTriangle, Archive, ArchiveRestore, Loader2 } from "lucide-react";
import { useState } from "react";

import { SectionLabel } from "components/AppShell";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";

import { useEventArchive, type EventArchiveState } from "./use-event-archive";

export const ARCHIVE_EXPLANATION =
  "Archived events leave the event list and stop sending mail and reminders. Participants can still read the event, and registration closes. You can restore it at any time.";
export const RESTORE_EXPLANATION =
  "The event returns to the event list, and mail, reminders and registration work again. Mail that was skipped while it was archived is not sent afterwards.";

function statusMessage(state: EventArchiveState, archived: boolean): string {
  switch (state.status) {
    case "submitting":
    case "watching":
      return state.command === "ARCHIVE_EVENT"
        ? "Archiving event…"
        : "Restoring event…";
    case "succeeded": {
      // A request that was superseded did not change the event itself, so
      // the text follows the event's actual state, not the request.
      const target = state.command === "ARCHIVE_EVENT";
      if (state.superseded && archived !== target) {
        return "A newer request changed this event.";
      }
      return target ? "Event archived." : "Event restored.";
    }
    default:
      return "";
  }
}

export function EventArchiveControl({
  eventId,
  eventTitle,
  uid,
  archived,
  onChanged,
}: {
  eventId: string;
  eventTitle: string;
  uid: string;
  /** The event's archived state from the page's own read. */
  archived: boolean;
  /** Called when a request settles, so the page reads the event again. */
  onChanged?: () => void;
}) {
  const { state, pending, submit, reset } = useEventArchive(
    eventId,
    uid,
    onChanged,
  );
  const [open, setOpen] = useState(false);
  const command = archived ? "RESTORE_EVENT" : "ARCHIVE_EVENT";
  const label = archived ? "Restore event" : "Archive event";
  const Icon = archived ? ArchiveRestore : Archive;

  return (
    <div className="space-y-3">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="min-h-11"
            disabled={pending}
            data-testid={`archive-event-${eventId}`}
          >
            <Icon className="mr-2 h-4 w-4" />
            {label}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {archived ? "Restore" : "Archive"} {eventTitle}?
            </DialogTitle>
            <DialogDescription>
              {archived ? RESTORE_EXPLANATION : ARCHIVE_EXPLANATION}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="min-h-11"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="cta"
              className="min-h-11"
              disabled={pending}
              data-testid="confirm-archive-event"
              onClick={() => {
                setOpen(false);
                void submit(command);
              }}
            >
              {label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p
        className="flex items-center gap-2 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
        data-testid="event-archive-status"
      >
        {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
        {statusMessage(state, archived)}
      </p>
      {state.status === "failed" && (
        <div
          className="flex flex-wrap items-start gap-2 text-sm text-destructive-text"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{state.error}</span>
          <Button
            variant="ghost"
            size="sm"
            className="min-h-11"
            onClick={reset}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

export function EventArchiveCard(props: {
  eventId: string;
  eventTitle: string;
  uid: string;
  archived: boolean;
  onChanged?: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 py-5">
        <SectionLabel>Archive</SectionLabel>
        <p className="text-sm text-muted-foreground">
          Take this event out of the event list and stop its mail, without
          deleting anything.
        </p>
        <EventArchiveControl {...props} />
      </CardContent>
    </Card>
  );
}
